import React, { useState, useEffect } from "react";
import {
  Mail,
  Download,
  Clock,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  LogOut,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send credentials so the httpOnly admin cookie is included automatically.
const adminApi = axios.create({ baseURL: API, withCredentials: true });

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [resumeStats, setResumeStats] = useState({ total_downloads: 0, recent_downloads: [] });
  const [loading, setLoading] = useState(false);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");

  // Check for existing session via httpOnly cookie (no browser storage).
  useEffect(() => {
    let cancelled = false;
    adminApi
      .get("/admin/me")
      .then(() => {
        if (!cancelled) setAuthenticated(true);
      })
      .catch(() => {
        if (!cancelled) setAuthenticated(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");

    try {
      const response = await adminApi.post("/admin/login", { password });
      if (response.data.authenticated) {
        setAuthenticated(true);
      }
    } catch (err) {
      setLoginError("Invalid password. Please try again.");
    } finally {
      setLoginLoading(false);
      setPassword("");
    }
  };

  const handleLogout = async () => {
    try {
      await adminApi.post("/admin/logout");
    } catch (_) {
      /* best-effort */
    }
    setAuthenticated(false);
    setMessages([]);
    setResumeStats({ total_downloads: 0, recent_downloads: [] });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactRes, resumeRes] = await Promise.all([
        adminApi.get("/admin/contact"),
        adminApi.get("/admin/resume-stats"),
      ]);
      setMessages(contactRes.data.messages || []);
      setTotalMessages(contactRes.data.total || 0);
      setResumeStats(resumeRes.data || { total_downloads: 0, recent_downloads: [] });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseUserAgent = (ua) => {
    if (!ua) return "Unknown";
    if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
    if (ua.includes("Edg")) return "Edge";
    return "Other";
  };

  // ─── Login Screen ────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="bg-[#09090b] min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-500/8 border border-blue-500/10 mb-5">
              <Lock size={24} className="text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Admin Access</h1>
            <p className="text-sm text-zinc-500">
              Enter your password to access the dashboard
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full px-4 py-3 pr-11 rounded-lg bg-white/[0.03] border border-white/8 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 focus:bg-blue-500/[0.02] transition-all duration-200"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {loginError && (
              <p className="text-sm text-red-400 text-center">{loginError}</p>
            )}

            <button
              type="submit"
              disabled={loginLoading || !password}
              className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginLoading ? "Authenticating..." : "Log In"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              Back to portfolio
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ─── Dashboard ───────────────────────────────────────
  return (
    <div className="bg-[#09090b] min-h-screen text-white">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors duration-200 text-sm"
            >
              <ArrowLeft size={16} />
              Back to site
            </a>
            <div className="w-px h-5 bg-white/10" />
            <h1 className="text-sm font-semibold text-white">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-zinc-400 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 hover:border-white/10 transition-all duration-200"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-zinc-500 hover:text-red-400 bg-white/5 hover:bg-red-500/5 rounded-lg border border-white/5 hover:border-red-500/15 transition-all duration-200"
            >
              <LogOut size={13} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/8 border border-blue-500/10 flex items-center justify-center">
                <Mail size={16} className="text-blue-400" />
              </div>
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Messages
              </span>
            </div>
            <p className="text-3xl font-bold text-white">{totalMessages}</p>
          </div>

          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/8 border border-emerald-500/10 flex items-center justify-center">
                <Download size={16} className="text-emerald-400" />
              </div>
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Resume Downloads
              </span>
            </div>
            <p className="text-3xl font-bold text-white">
              {resumeStats.total_downloads}
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-violet-500/8 border border-violet-500/10 flex items-center justify-center">
                <BarChart3 size={16} className="text-violet-400" />
              </div>
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Engagement Rate
              </span>
            </div>
            <p className="text-3xl font-bold text-white">
              {totalMessages + resumeStats.total_downloads > 0
                ? Math.round(
                    (totalMessages /
                      (totalMessages + resumeStats.total_downloads)) *
                      100
                  )
                : 0}
              <span className="text-sm text-zinc-500 font-normal ml-1">%</span>
            </p>
            <p className="text-[11px] text-zinc-600 mt-1">
              Messages vs total interactions
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/5">
          <button
            onClick={() => setActiveTab("messages")}
            className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
              activeTab === "messages"
                ? "text-blue-400 border-blue-400"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
          >
            <span className="flex items-center gap-2">
              <Mail size={14} />
              Contact Messages
              {totalMessages > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
                  {totalMessages}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("downloads")}
            className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
              activeTab === "downloads"
                ? "text-blue-400 border-blue-400"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
          >
            <span className="flex items-center gap-2">
              <Download size={14} />
              Resume Downloads
              {resumeStats.total_downloads > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                  {resumeStats.total_downloads}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={20} className="animate-spin text-zinc-500" />
          </div>
        )}

        {/* Messages Tab */}
        {!loading && activeTab === "messages" && (
          <div>
            {messages.length === 0 ? (
              <div className="text-center py-20">
                <Mail size={32} className="text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No messages yet</p>
                <p className="text-zinc-700 text-xs mt-1">
                  Messages from your contact form will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className="rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all duration-200 overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setExpandedMessage(
                          expandedMessage === index ? null : index
                        )
                      }
                      className="w-full p-5 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-400">
                            {msg.name ? msg.name.charAt(0).toUpperCase() : "?"}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {msg.name}
                          </p>
                          <p className="text-xs text-zinc-500 truncate">
                            {msg.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        <span className="text-xs text-zinc-600 hidden sm:block">
                          {formatDate(msg.timestamp)}
                        </span>
                        {expandedMessage === index ? (
                          <ChevronUp size={14} className="text-zinc-500" />
                        ) : (
                          <ChevronDown size={14} className="text-zinc-500" />
                        )}
                      </div>
                    </button>

                    {expandedMessage === index && (
                      <div className="px-5 pb-5 pt-0 border-t border-white/5">
                        <div className="mt-4 p-4 rounded-lg bg-white/[0.02]">
                          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                            {msg.message}
                          </p>
                        </div>
                        <div className="mt-3 flex items-center gap-4">
                          <a
                            href={`mailto:${msg.email}`}
                            className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <Mail size={12} />
                            Reply
                          </a>
                          <span className="text-xs text-zinc-600 sm:hidden">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Downloads Tab */}
        {!loading && activeTab === "downloads" && (
          <div>
            {resumeStats.recent_downloads.length === 0 ? (
              <div className="text-center py-20">
                <Download size={32} className="text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No downloads yet</p>
                <p className="text-zinc-700 text-xs mt-1">
                  Resume download events will appear here
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-white/5 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider hidden sm:table-cell">
                        Browser
                      </th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider hidden md:table-cell">
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumeStats.recent_downloads.map((dl, index) => (
                      <tr
                        key={dl.id || index}
                        className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-5 py-3.5 text-xs text-zinc-600 font-mono">
                          {index + 1}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <Clock size={12} className="text-zinc-600" />
                            <span className="text-sm text-zinc-400">
                              {formatDate(dl.timestamp)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-zinc-500 hidden sm:table-cell">
                          {parseUserAgent(dl.user_agent)}
                        </td>
                        <td className="px-5 py-3.5 text-xs text-zinc-600 font-mono hidden md:table-cell">
                          {dl.ip_address || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
